import {
  Box,
  Flex,
  Icon,
  IconButton,
  Image,
  LightMode,
  Stack,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import capitalize from "@splitfire-agency/raiden-library/dist/libraries/utils/capitalize";
import { AnimatePresence, motion } from "framer-motion";
import { memo, useMemo } from "react";
import { IoCheckmarkOutline, IoTrashOutline } from "react-icons/io5";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import AnimatedButton from "../../../../components/ButtonAnimated";
import ImageLoaderHandler from "../../../../components/ImageLoaderHandler";
import {
  PRESTATIONS_HOLDER_TYPES,
  PRESTATIONS_PRICES_TYPES,
} from "../../../../constants/prestations";
import { SUBSCRIPTIONS_TYPES_VALUE_PER_CUSTOMER } from "../../../../constants/subscriptions";
import generateFrontPath from "../../../../libraries/utils/generateFrontPath";
import generatePampaUri from "../../../../libraries/utils/generatePampaUri";
import DialogSelect from "./DialogSelect";

/** @type {import("framer-motion").Variants} */
const HEIGHT_VARIANTS = {
  hidden: {
    height: 0,
    transition: {
      when: "afterChildren",
    },
  },
  visible: {
    height: "auto",
    transition: {
      when: "beforeChildren",
    },
  },
};

/** @type {import("framer-motion").Variants} */
const BUTTON_DELETE_VARIANTS = {
  hidden: {
    width: 0,
    transition: {
      when: "afterChildren",
    },
  },
  visible: {
    width: "auto",
    transition: {
      delay: 0.25,
      when: "beforeChildren",
    },
  },
};

/** @type {import("framer-motion").Variants} */
const OPACITY_VARIANTS = {
  hidden: {
    opacity: 0,
    transition: {
      type: "tween",
      duration: 0.25,
    },
  },
  visible: {
    opacity: 1,
    transition: {
      type: "tween",
      duration: 0.25,
    },
  },
};

const SubscriptionsBatchCard = memo(
  /**
   * @typedef {Object} Props
   * @property {import("@raiden/library/types/Subscription").Subscription} subscription
   * @property {import("@raiden/library/types/Holder").Holder[]} holders
   * @property {string[]} [initialHolderIds]
   * @property {(param0: { subscriptionId: string; holderIds: string[] }) => void} upsertSubscriptionData
   * @property {(param0: { subscriptionId: string }) => void} deleteSubscriptionData
   * @property {boolean} isSelected
   *
   * @param {Props} props
   */
  function Card({
    subscription,
    holders,
    initialHolderIds = [],
    upsertSubscriptionData,
    deleteSubscriptionData,
    isSelected,
  }) {
    const intl = useIntl();

    const { isOpen, onOpen, onClose } = useDisclosure();

    const holderTypes = useMemo(() => {
      const formattedList = intl.formatList(
        (subscription.prestations_holder_types ?? []).map((holderType) =>
          PRESTATIONS_HOLDER_TYPES[holderType]?.labelPlural
            ? intl.formatMessage(
                PRESTATIONS_HOLDER_TYPES[holderType].labelPlural,
              )
            : holderType,
        ),
        { type: "conjunction" },
      );
      // formattedList returns an empty array if its given value is an empty array
      return Array.isArray(formattedList) ? "" : capitalize(formattedList);
    }, [intl, subscription.prestations_holder_types]);

    function handleClickDelete() {
      deleteSubscriptionData({ subscriptionId: String(subscription.id) });
    }

    const cover = useMemo(() => {
      // find the first cover from the subscription's prestations if the prestations has a cover
      const prestationWithCover = subscription.prestations?.find(
        (prestation) => prestation.post?.current_revision?.cover,
      );
      return prestationWithCover?.post?.current_revision?.cover ?? null;
    }, [subscription.prestations]);

    return (
      <>
        <DialogSelect
          subscription={subscription}
          holders={holders}
          isSelected={isSelected}
          initialHolderIds={initialHolderIds}
          upsertSubscriptionsData={upsertSubscriptionData}
          deleteSubscriptionsData={deleteSubscriptionData}
          isOpen={isOpen}
          onClose={onClose}
        />

        <LightMode>
          <Box
            boxShadow={
              isSelected
                ? "0 0 0 2px var(--chakra-colors-brandPrimary-500)"
                : "none"
            }
            backgroundColor="gray.50"
            borderRadius="0.375rem"
            flexShrink={0}
            overflow="hidden"
            position="relative"
            transition="all .25s ease">
            <Box
              position="absolute"
              top="0"
              left="0"
              opacity={isSelected ? 1 : 0}
              transition="all .25s ease">
              <Box
                position="absolute"
                top="0"
                left="0"
                borderTop="4.5rem solid #fff"
                borderRight="4.5rem solid transparent"
              />

              <Icon
                as={IoCheckmarkOutline}
                boxSize="2rem"
                color="brandPrimary.500"
                position="absolute"
                top="0.5rem"
                left="0.5rem"
                transform={isSelected ? "scale(1)" : "scale(0)"}
                transition="all .25s ease"
              />
            </Box>

            <Stack flexDirection={{ base: "column", lg: "row" }} spacing="0">
              <Box
                position="relative"
                alignSelf="stretch"
                flexShrink={0}
                w="full"
                maxW={{ base: "none", lg: "300px" }}
                minHeight="158px">
                <ImageLoaderHandler
                  src={
                    cover
                      ? generatePampaUri({
                          image: cover,
                        })
                      : generateFrontPath({
                          id: "internal-assets",
                          parameters: {
                            filePath: "placeholder-subscription.svg",
                          },
                          includeBasePath: true,
                        })
                  }>
                  {({ src, isLoaded, handleLoad }) => (
                    <Image
                      src={src}
                      onLoad={handleLoad}
                      opacity={isLoaded ? 1 : 0}
                      transition="opacity .25s ease"
                      alt={subscription.name}
                      objectFit="cover"
                      w="full"
                      h="full"
                    />
                  )}
                </ImageLoaderHandler>
              </Box>

              <Stack
                flexGrow={1}
                direction={{ base: "column", lg: "row" }}
                alignItems="flex-start"
                spacing="1rem"
                p="1rem">
                <Stack flexGrow={1} spacing="1rem">
                  <Text
                    fontSize="1.25rem"
                    fontWeight={400}
                    lineHeight="1.75rem">
                    {subscription.name}
                  </Text>

                  {subscription.infos && (
                    <Text
                      fontSize="0.875rem"
                      fontWeight={400}
                      lineHeight="1.25rem"
                      whiteSpace="pre-wrap">
                      {subscription.infos}
                    </Text>
                  )}

                  {holderTypes && (
                    <Text
                      fontSize="0.875rem"
                      fontWeight={400}
                      lineHeight="1.25rem">
                      {holderTypes}
                    </Text>
                  )}
                </Stack>

                <Stack
                  flexShrink={0}
                  spacing="1rem"
                  alignItems="flex-end"
                  alignSelf="stretch"
                  minW="13rem">
                  <Stack flexGrow={1} spacing="0.375rem">
                    {subscription.estimated_price && (
                      <>
                        <Text
                          fontSize="1rem"
                          fontWeight={400}
                          lineHeight="1.5rem"
                          textAlign="right">
                          <FormattedNumber
                            value={subscription.estimated_price.amount}
                            style="currency"
                            currency="EUR"
                          />
                        </Text>

                        <Text
                          fontSize="1rem"
                          fontWeight={400}
                          lineHeight="1.25rem"
                          textAlign="right">
                          {intl.formatMessage(
                            PRESTATIONS_PRICES_TYPES[
                              subscription.estimated_price.type
                            ].label,
                          )}
                        </Text>
                      </>
                    )}
                  </Stack>

                  <Box>
                    <AnimatePresence>
                      {initialHolderIds.length > 0 && (
                        <motion.div
                          variants={HEIGHT_VARIANTS}
                          initial="hidden"
                          animate="visible"
                          exit="hidden">
                          <motion.div variants={OPACITY_VARIANTS}>
                            <Flex justify="flex-end" pb="1rem">
                              <Tag
                                variant="outline"
                                borderRadius="full"
                                colorScheme="teal"
                                size="lg">
                                <TagLeftIcon
                                  as={IoCheckmarkOutline}
                                  boxSize="1rem"
                                  mr="0.5rem"
                                />

                                <TagLabel>
                                  <FormattedMessage
                                    defaultMessage="{count, plural, =0 {aucun adhérent inscrit} one {# adhérent inscrit} other {# adhérents inscrits}}"
                                    values={{ count: initialHolderIds.length }}
                                  />
                                </TagLabel>
                              </Tag>
                            </Flex>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <AnimatePresence>
                      {isSelected &&
                        subscription.type ===
                          SUBSCRIPTIONS_TYPES_VALUE_PER_CUSTOMER && (
                          <motion.div
                            variants={HEIGHT_VARIANTS}
                            initial="hidden"
                            animate="visible"
                            exit="hidden">
                            <motion.div variants={OPACITY_VARIANTS}>
                              <Flex justify="flex-end" pb="1rem">
                                <Tag
                                  variant="outline"
                                  borderRadius="full"
                                  colorScheme="teal"
                                  size="lg">
                                  <TagLeftIcon
                                    as={IoCheckmarkOutline}
                                    boxSize="1rem"
                                    mr="0.5rem"
                                  />

                                  <TagLabel>
                                    <FormattedMessage
                                      defaultMessage="Selectionnée"
                                      values={{
                                        count: initialHolderIds.length,
                                      }}
                                    />
                                  </TagLabel>
                                </Tag>
                              </Flex>
                            </motion.div>
                          </motion.div>
                        )}
                    </AnimatePresence>

                    <Flex justify="flex-end">
                      <AnimatedButton
                        onClick={onOpen}
                        size="sm"
                        colorScheme="brandPrimary"
                        label={
                          isSelected
                            ? intl.formatMessage({ defaultMessage: "Modifier" })
                            : intl.formatMessage({
                                defaultMessage: "Choisir cette activité",
                              })
                        }
                      />

                      <AnimatePresence>
                        {isSelected && (
                          <motion.div
                            variants={BUTTON_DELETE_VARIANTS}
                            initial="hidden"
                            animate="visible"
                            exit="hidden">
                            <motion.div variants={OPACITY_VARIANTS}>
                              <Box pl=".5rem">
                                <Tooltip
                                  label={intl.formatMessage({
                                    defaultMessage: "Supprimer",
                                  })}>
                                  <IconButton
                                    icon={<Icon as={IoTrashOutline} />}
                                    onClick={handleClickDelete}
                                    size="sm"
                                    colorScheme="red"
                                    aria-label={intl.formatMessage({
                                      defaultMessage: "Supprimer",
                                    })}
                                  />
                                </Tooltip>
                              </Box>
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Flex>
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </LightMode>
      </>
    );
  },
);

export default SubscriptionsBatchCard;
